import { Content as TContent, Markdown } from '@/lib/markdown';
import Button from './Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconName } from '@fortawesome/pro-regular-svg-icons';
import { Lines } from './Lines';
import { TextImageSection } from './TextImageSection';

export interface IContent {
	priority?: boolean;
	h1?: boolean;
	disableImageMaxHeight?: boolean;
	disableContainer?: boolean;
	className?: string;
	caption?: string | React.ReactNode;
	title?: string | React.ReactNode;
	xlTitle?: string;
	description?: TContent;
	iconList?: {
		iconName: string;
		text?: string;
	}[];
	buttons?: {
		color?: 'dark' | 'primary' | 'neutral';
		variant?: 'contained' | 'outlined';
		iconName?: string;
		text: string;
		link: string;
	}[];
	image: {
		url: string;
		title: string;
	};
	imageCover?: 'contain' | 'cover';
	reverse?: boolean;
	reverseOnMobile?: boolean;
	justifyContent?: 'justify-center' | 'justify-start' | 'justify-end';
}

export default function Content(props: IContent) {
	// Build the content ReactNode
	const contentNodes: React.ReactNode[] = [];

	// Add xlTitle if present
	if (props.xlTitle) {
		contentNodes.push(
			<h2 key="xlTitle" className="!text-4xl sm:!text-5xl md:!text-7xl font-bold text-primary-600 mb-6 lg:mb-12">
				{props.xlTitle}
			</h2>
		);
	}

	// Add caption if present
	if (props.caption) {
		contentNodes.push(
			typeof props.caption === 'string' ? (
				<p key="caption" className="font-bold mt-4 text-primary-500 text-2xl mb-4">
					{props.caption}
				</p>
			) : (
				<div key="caption">{props.caption}</div>
			)
		);
	}

	// Add Lines if h1
	if (props.h1) {
		contentNodes.push(<Lines key="lines" fill="white" size={150} />);
	}

	// Add description (Markdown)
	if (props.description?.json) {
		contentNodes.push(
			<div key="description" className="[&_p]:text-primary-500">
				<Markdown content={props.description} />
			</div>
		);
	}

	// Add iconList
	if (props.iconList?.length) {
		contentNodes.push(
			<div key="iconList" className="flex flex-col gap-4">
				{props.iconList.map((icon, index) => {
					return (
						<div key={index} className="flex items-center gap-4">
							<FontAwesomeIcon
								className="text-primary-500 size-5"
								icon={['far', icon.iconName as IconName]}
							/>
							<p className="text-md font-medium">{icon.text}</p>
						</div>
					);
				})}
			</div>
		);
	}

	// Add buttons
	if (props.buttons?.length) {
		contentNodes.push(
			<div key="buttons" className="flex items-center gap-4">
				{props.buttons.map((button, index) => {
					return (
						<Button
							variant={button?.variant ?? 'outlined'}
							color={button.color}
							startIcon={
								button.iconName && (
									<FontAwesomeIcon
										icon={['far', button.iconName as IconName]}
									/>
								)
							}
							key={index}
							href={button.link}
						>
							{button.text}
						</Button>
					);
				})}
			</div>
		);
	}

	// Determine title for TextImageSection
	let sectionTitle: string = '';
	if (typeof props.title === 'string') {
		sectionTitle = props.title;
	} else if (props.title) {
		// If title is a ReactNode, we'll include it in content instead
		contentNodes.unshift(<div key="titleNode">{props.title}</div>);
	}

	// Custom styling wrapper to preserve original Content styling
	const contentWrapper = (
		<div className={`flex flex-col gap-4 lg:gap-6 ${props.justifyContent || 'justify-center'}`}>
			{contentNodes}
		</div>
	);

	return (
		<TextImageSection
			title={sectionTitle}
			content={contentWrapper}
			imageUrl={props.image?.url || ''}
			imageAlt={props.image?.title || ''}
			reverse={props.reverse}
			backgroundColor="bg-white"
			sectionClassName={`${props.disableContainer ? '' : 'container'} ${props.className || ''}`}
		/>
	);
}
